import { useCallback, useEffect, useMemo, useState } from "react";
import { getMerchGarmentAsset, merchGarmentAssets } from "../../data/merchGarmentAssets";
import { createIdentityBackSvg, createIdentityFrontSvg } from "../../merch/artwork/identityArtwork";
import { getConfiguredGarmentView, getMerchGarmentConfig } from "../../merch/mockup/merchGarmentConfigs";
import {
  DEFAULT_GARMENT_ID,
  GARMENT_CALIBRATION_LIMITS,
  GARMENT_COLORS,
  IDENTITY_BACK_DEFAULT_PLACEMENT,
  IDENTITY_BACK_PLACEMENT_LIMITS,
  IDENTITY_FRONT_DEFAULT_PLACEMENT,
  IDENTITY_FRONT_PLACEMENT_LIMITS,
  IDENTITY_FRONT_PRINT_WIDTH_INCHES,
  IDENTITY_BACK_PRINT_WIDTH_INCHES,
  IDENTITY_BACK_PRINT_WIDTH_RATIO,
  IDENTITY_BACK_VISIBLE_ASPECT_RATIO_ESTIMATE,
  IDENTITY_FRONT_PRINT_WIDTH_RATIO,
  IDENTITY_FRONT_VISIBLE_ASPECT_RATIO,
  PRINTFUL_DTFLEX_MAX_HEIGHT_INCHES,
  PRINTFUL_DTFLEX_MAX_WIDTH_INCHES,
  PRINT_TECHNIQUE,
  MOCKUP_SCHEMA_VERSION,
  MOCKUP_STORAGE_KEY,
  getGarmentColor
} from "../../merch/mockup/merchMockupConstants";
import MerchGarmentMockup from "./MerchGarmentMockup";
import MerchPlacementControls from "./MerchPlacementControls";
import MerchPrintNotes from "./MerchPrintNotes";
import MerchReadinessChecklist from "./MerchReadinessChecklist";
import { downloadAllIdentityProductionFiles } from "../../utils/merchArtworkPackageExport";
import { calculatePrintDimensions } from "../../utils/merchPrintSizing";
import { cardStyle, sectionLabelStyle } from "./MerchStyles";

const DEFAULT_PREFERENCES = {
  schemaVersion: MOCKUP_SCHEMA_VERSION,
  selectedGarment: DEFAULT_GARMENT_ID,
  selectedColor: "Faded Black",
  selectedView: "back",
  frontPlacement: IDENTITY_FRONT_DEFAULT_PLACEMENT,
  backPlacement: IDENTITY_BACK_DEFAULT_PLACEMENT,
  calibrationOverrides: {},
  calibrateMode: false,
  previewSizeMode: "visual",
  zoom: "fit",
  showPrintBoundary: false,
  showRulers: false,
  showMeasurements: false,
  showFrontAndBack: false,
  printNotes: { approvalStatus: "Needs Review", notes: "", lastUpdated: null },
  readinessChecks: { frontBoundaryReviewed: false, backBoundaryReviewed: false, frontPlacementReviewed: false, backPlacementReviewed: false, printNotesReviewed: false }
};

let hasLoggedTrueSizeDebug = false;

function MerchMockupStudio({ onCurrentMockupChange }) {
  const [preferences, setPreferences] = useState(loadPreferences);
  const [calibrationStatus, setCalibrationStatus] = useState("");
  const [exportState, setExportState] = useState({ running: false, message: "" });
  const garmentConfig = getMerchGarmentConfig(preferences.selectedGarment);
  const garmentColor = getGarmentColor(preferences.selectedColor);
  const artworkStrings = useMemo(() => ({
    frontDark: createIdentityFrontSvg({ garmentTone: "dark" }),
    frontLight: createIdentityFrontSvg({ garmentTone: "light" }),
    backDark: createIdentityBackSvg({ garmentTone: "dark" }),
    backLight: createIdentityBackSvg({ garmentTone: "light" })
  }), []);
  const frontDarkUrl = useSvgBlobUrl(artworkStrings.frontDark);
  const frontLightUrl = useSvgBlobUrl(artworkStrings.frontLight);
  const backDarkUrl = useSvgBlobUrl(artworkStrings.backDark);
  const backLightUrl = useSvgBlobUrl(artworkStrings.backLight);
  const artworkUrls = useMemo(() => ({
    frontDark: frontDarkUrl,
    frontLight: frontLightUrl,
    backDark: backDarkUrl,
    backLight: backLightUrl
  }), [frontDarkUrl, frontLightUrl, backDarkUrl, backLightUrl]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development" || hasLoggedTrueSizeDebug) return;
    hasLoggedTrueSizeDebug = true;
    console.info("[Merch Engine] DTFlex sizing", ["front", "back"].map(createTrueSizeDebug));
  }, []);

  useEffect(() => {
    try { localStorage.setItem(MOCKUP_STORAGE_KEY, JSON.stringify(preferences)); }
    catch (error) { console.warn("[Merch Engine] Mockup Studio preferences could not be saved.", error); }
  }, [preferences]);

  const update = (changes) => setPreferences((current) => ({ ...current, ...changes, schemaVersion: MOCKUP_SCHEMA_VERSION }));
  const getViewGeometry = useCallback((view) => getConfiguredGarmentView(
    garmentConfig,
    view,
    preferences.calibrationOverrides?.[garmentConfig.id]?.[view]
  ), [garmentConfig, preferences.calibrationOverrides]);
  const activeGeometry = getViewGeometry(preferences.selectedView);

  useEffect(() => {
    if (!onCurrentMockupChange) return;
    const mockups = GARMENT_COLORS.flatMap((color) => ["front", "back"].map((view) => ({
      garmentImage: getMerchGarmentAsset(garmentConfig.id, color.id, garmentConfig.imageKeys[view]),
      artworkUrl: getArtworkUrl(artworkUrls, color, view),
      garmentConfig,
      garmentColor: color.id,
      view,
      placement: view === "front" ? preferences.frontPlacement : preferences.backPlacement,
      viewGeometry: getViewGeometry(view),
      previewSizeMode: preferences.previewSizeMode,
      printWidthRatio: view === "front" ? IDENTITY_FRONT_PRINT_WIDTH_RATIO : IDENTITY_BACK_PRINT_WIDTH_RATIO
    })));
    mockups.sort((first, second) => {
      const firstSelected = first.garmentColor === garmentColor.id && first.view === preferences.selectedView;
      const secondSelected = second.garmentColor === garmentColor.id && second.view === preferences.selectedView;
      return Number(secondSelected) - Number(firstSelected);
    });
    onCurrentMockupChange(mockups);
  }, [onCurrentMockupChange, garmentConfig, garmentColor, preferences.selectedView, preferences.previewSizeMode, preferences.frontPlacement, preferences.backPlacement, getViewGeometry, artworkUrls]);

  const updateActiveGeometry = (viewGeometry) => {
    update({
      calibrationOverrides: {
        ...preferences.calibrationOverrides,
        [garmentConfig.id]: {
          ...(preferences.calibrationOverrides?.[garmentConfig.id] || {}),
          [preferences.selectedView]: viewGeometry
        }
      }
    });
    setCalibrationStatus("");
  };

  const resetPlacement = () => update(preferences.selectedView === "front"
    ? { frontPlacement: { ...IDENTITY_FRONT_DEFAULT_PLACEMENT } }
    : { backPlacement: { ...IDENTITY_BACK_DEFAULT_PLACEMENT } });

  const resetCalibration = () => {
    const garmentOverrides = { ...(preferences.calibrationOverrides?.[garmentConfig.id] || {}) };
    delete garmentOverrides[preferences.selectedView];
    update({ calibrationOverrides: { ...preferences.calibrationOverrides, [garmentConfig.id]: garmentOverrides } });
    setCalibrationStatus("Calibration reset.");
  };

  const resetViewSettings = () => update({ previewSizeMode: "visual", zoom: "fit", showPrintBoundary: false, showRulers: false, showMeasurements: false, showFrontAndBack: false });

  const copyCalibration = async () => {
    const payload = { garmentId: garmentConfig.id, view: preferences.selectedView, printTechnique: PRINT_TECHNIQUE, maxPrintWidthInches: PRINTFUL_DTFLEX_MAX_WIDTH_INCHES, maxPrintHeightInches: PRINTFUL_DTFLEX_MAX_HEIGHT_INCHES, ...activeGeometry };
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable.");
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCalibrationStatus("Calibration JSON copied.");
    } catch (error) {
      console.error("[Merch Engine] Calibration copy failed:", error);
      setCalibrationStatus("Calibration copy failed. Check browser permissions.");
    }
  };

  const resetAll = () => {
    if (!window.confirm("Reset all Mockup Studio settings, calibration, notes, and review checks?")) return;
    try { localStorage.removeItem(MOCKUP_STORAGE_KEY); }
    catch (error) { console.warn("[Merch Engine] Mockup Studio preferences could not be cleared.", error); }
    setPreferences(cloneDefaults());
    setCalibrationStatus("");
  };

  const exportAllFiles = async () => {
    setExportState({ running: true, message: "Exporting 0 of 8…" });
    const failures = await downloadAllIdentityProductionFiles({ onProgress: (completed, total) => setExportState({ running: true, message: `Exporting ${completed} of ${total}…` }) });
    setExportState({ running: false, message: failures.length ? `Export finished with failures: ${failures.map((item) => item.filename).join(", ")}` : "All 8 production files downloaded." });
  };

  const automaticChecks = useMemo(() => {
    const garmentAssets = merchGarmentAssets[DEFAULT_GARMENT_ID];
    const frontDimensions = getIdentityPrintDimensions("front");
    const backDimensions = getIdentityPrintDimensions("back");
    return [
      { label: "DTFlex selected", complete: PRINT_TECHNIQUE === "DTFlex" },
      { label: "Front artwork width is 4.0\"", complete: IDENTITY_FRONT_PRINT_WIDTH_INCHES === 4 },
      { label: "Back artwork width is 11.0\"", complete: IDENTITY_BACK_PRINT_WIDTH_INCHES === 11 },
      { label: "Front artwork fits inside 12\" × 16\"", complete: frontDimensions.isValid },
      { label: "Back artwork fits inside 12\" × 16\"", complete: backDimensions.isValid },
      ...GARMENT_COLORS.flatMap((color) => ["front", "back"].map((view) => ({ label: `${color.id} ${view} mockup loaded`, complete: Boolean(garmentAssets?.[color.id]?.[view]) }))),
      { label: "Front dark SVG available", complete: Boolean(artworkStrings.frontDark) },
      { label: "Front light SVG available", complete: Boolean(artworkStrings.frontLight) },
      { label: "Back dark SVG available", complete: Boolean(artworkStrings.backDark) },
      { label: "Back light SVG available", complete: Boolean(artworkStrings.backLight) },
      { label: "All eight production artwork files available", complete: Boolean(artworkStrings.frontDark && artworkStrings.frontLight && artworkStrings.backDark && artworkStrings.backLight) }
    ];
  }, [artworkStrings]);

  const mainViews = preferences.showFrontAndBack ? ["front", "back"] : [preferences.selectedView];

  return (
    <section aria-labelledby="mockup-studio-heading">
      <MockupResponsiveStyles />
      <div style={{ maxWidth: "760px", marginBottom: "34px" }}>
        <p style={sectionLabelStyle}>Placement Review</p>
        <h2 id="mockup-studio-heading" style={{ margin: "0 0 12px", fontSize: "clamp(30px, 5vw, 50px)", letterSpacing: "-.035em" }}>Identity Tee Mockup Studio</h2>
        <p style={{ margin: "0 0 10px", color: "#999", lineHeight: 1.65 }}>Mockups are for visual placement review only. Production files remain 4500 × 5400 px with transparent backgrounds. Set the artwork to 4.0&quot; front and 11.0&quot; back inside Printful&apos;s 12&quot; × 16&quot; DTFlex print area.</p>
        <p style={{ margin: 0, color: "#6f6f6f", fontSize: "12px", lineHeight: 1.55 }}>Use Printful-generated or otherwise authorized mockup images for production use. Do not scrape product-page photography.</p>
      </div>

      <div className="merch-mockup-layout">
        <div style={{ ...cardStyle, padding: "clamp(16px, 3vw, 28px)", overflow: "hidden" }}>
          <div className="merch-zoom-viewport" style={{ overflow: "auto", maxHeight: preferences.zoom === "fit" ? "none" : "760px" }}>
            <div className={preferences.showFrontAndBack ? "merch-main-mockups split" : "merch-main-mockups"} style={{ width: preferences.zoom === "fit" ? "100%" : `${preferences.zoom}%`, minWidth: preferences.zoom === "50" ? "50%" : "100%", margin: "0 auto" }}>
              {mainViews.map((view) => <MockupView key={view} view={view} garmentColor={garmentColor} garmentConfig={garmentConfig} artworkUrls={artworkUrls} preferences={preferences} geometry={getViewGeometry(view)} update={update} main />)}
            </div>
          </div>
          {preferences.previewSizeMode === "trueSize" && <p style={{ margin: "14px 0 0", color: "#999", fontSize: "12px", lineHeight: 1.5 }}>Active DTFlex width: {preferences.selectedView === "front" ? "4.0\" (33.3%)" : "11.0\" (91.7%)"} of the 12&quot; print area. True Size preview is an alignment reference; confirm final placement in Printful before ordering.</p>}
        </div>

        <MerchPlacementControls
          garmentConfig={garmentConfig}
          selectedColor={preferences.selectedColor}
          onColorChange={(selectedColor) => update({ selectedColor })}
          selectedView={preferences.selectedView}
          onViewChange={(selectedView) => { update({ selectedView }); setCalibrationStatus(""); }}
          frontPlacement={preferences.frontPlacement}
          onFrontPlacementChange={(frontPlacement) => update({ frontPlacement })}
          backPlacement={preferences.backPlacement}
          onBackPlacementChange={(backPlacement) => update({ backPlacement })}
          showPrintBoundary={preferences.showPrintBoundary}
          onShowPrintBoundaryChange={(showPrintBoundary) => update({ showPrintBoundary })}
          showFrontAndBack={preferences.showFrontAndBack}
          onShowFrontAndBackChange={(showFrontAndBack) => update({ showFrontAndBack })}
          previewSizeMode={preferences.previewSizeMode}
          onPreviewSizeModeChange={(previewSizeMode) => update({ previewSizeMode })}
          zoom={preferences.zoom}
          onZoomChange={(zoom) => update({ zoom })}
          showRulers={preferences.showRulers}
          onShowRulersChange={(showRulers) => update({ showRulers })}
          showMeasurements={preferences.showMeasurements}
          onShowMeasurementsChange={(showMeasurements) => update({ showMeasurements })}
          calibrateMode={preferences.calibrateMode}
          onCalibrateModeChange={(calibrateMode) => update({ calibrateMode })}
          viewGeometry={activeGeometry}
          onViewGeometryChange={updateActiveGeometry}
          onCopyCalibration={copyCalibration}
          calibrationStatus={calibrationStatus}
          onResetCalibration={resetCalibration}
          onResetViewSettings={resetViewSettings}
          onResetPlacement={resetPlacement}
          onResetAll={resetAll}
        />
      </div>

      <GarmentInformation garmentConfig={garmentConfig} />

      <section style={{ marginTop: "clamp(60px, 9vw, 96px)" }} aria-labelledby="mockup-comparison-heading">
        <p style={sectionLabelStyle}>Color Comparison</p>
        <h2 id="mockup-comparison-heading" style={{ margin: "0 0 28px", fontSize: "clamp(28px, 4vw, 42px)" }}>{preferences.selectedView === "front" ? "Front" : "Back"} across all garment tones</h2>
        <div className="merch-mockup-comparison">
          {GARMENT_COLORS.map((color) => <MockupView key={color.id} view={preferences.selectedView} garmentColor={color} garmentConfig={garmentConfig} artworkUrls={artworkUrls} preferences={preferences} geometry={activeGeometry} compact />)}
        </div>
      </section>

      <div className="merch-print-lab-panels" style={{ marginTop: "clamp(60px, 9vw, 96px)" }}>
        <MerchPrintNotes notes={preferences.printNotes} onSave={(printNotes) => update({ printNotes })} />
        <MerchReadinessChecklist automaticChecks={automaticChecks} manualChecks={preferences.readinessChecks} onManualChecksChange={(readinessChecks) => update({ readinessChecks })} approvalStatus={preferences.printNotes.approvalStatus} />
      </div>

      <section style={{ ...cardStyle, marginTop: "24px", padding: "24px" }} aria-labelledby="production-package-heading">
        <h2 id="production-package-heading" style={{ margin: "0 0 10px", fontSize: "22px" }}>Production Artwork Package</h2>
        <p style={{ margin: "0 0 18px", color: "#999", lineHeight: 1.55 }}>Downloads all eight transparent production files separately. Your browser may ask permission for multiple downloads.</p>
        <button type="button" disabled={exportState.running} onClick={exportAllFiles} style={{ minHeight: "48px", padding: "0 18px", border: "1px solid #e10600", borderRadius: "8px", background: exportState.running ? "#242424" : "#e10600", color: "white", fontWeight: 900, cursor: exportState.running ? "not-allowed" : "pointer" }}>{exportState.running ? "Exporting…" : "Download All Production Files"}</button>
        <p aria-live="polite" style={{ minHeight: "20px", margin: "12px 0 0", color: "#aaa", fontSize: "13px" }}>{exportState.message}</p>
      </section>
    </section>
  );
}

function MockupView({ view, garmentColor, garmentConfig, artworkUrls, preferences, geometry, update, main = false, compact = false }) {
  const placement = view === "front" ? preferences.frontPlacement : preferences.backPlacement;
  const imageKey = garmentConfig.imageKeys[view];
  const garmentImage = getMerchGarmentAsset(garmentConfig.id, garmentColor.id, imageKey);
  const mockup = (
    <MerchGarmentMockup
      garmentConfig={garmentConfig}
      garmentImage={garmentImage}
      imageAlt={`${garmentColor.id} ${garmentConfig.name}, ${view} view`}
      color={garmentColor}
      selectedColor={garmentColor.id}
      view={view}
      artworkUrl={getArtworkUrl(artworkUrls, garmentColor, view)}
      placement={placement}
      placementLimits={view === "front" ? IDENTITY_FRONT_PLACEMENT_LIMITS : IDENTITY_BACK_PLACEMENT_LIMITS}
      viewGeometry={geometry}
      previewSizeMode={preferences.previewSizeMode}
      printWidthInches={view === "front" ? IDENTITY_FRONT_PRINT_WIDTH_INCHES : IDENTITY_BACK_PRINT_WIDTH_INCHES}
      printWidthRatio={view === "front" ? IDENTITY_FRONT_PRINT_WIDTH_RATIO : IDENTITY_BACK_PRINT_WIDTH_RATIO}
      defaultPlacement={view === "front" ? IDENTITY_FRONT_DEFAULT_PLACEMENT : IDENTITY_BACK_DEFAULT_PLACEMENT}
      showRulers={main && preferences.showRulers}
      showMeasurements={main && preferences.showMeasurements}
      onPlacementChange={main ? (nextPlacement) => update(view === "front" ? { frontPlacement: nextPlacement } : { backPlacement: nextPlacement }) : undefined}
      movable={main && view === preferences.selectedView}
      showPrintBoundary={preferences.showPrintBoundary}
      compact={compact}
    />
  );
  if (main) return <div><p style={mockupLabelStyle}>{garmentColor.id} — {view}</p>{mockup}</div>;
  return <article style={{ ...cardStyle, padding: "14px" }}>{mockup}<h3 style={{ margin: "15px 4px 5px", fontSize: "17px" }}>{garmentColor.id}</h3><p style={{ margin: "0 4px 5px", color: "#777", fontSize: "12px" }}>{garmentColor.artworkTone === "dark" ? "Dark" : "Light"} production artwork</p></article>;
}

function GarmentInformation({ garmentConfig }) {
  const measurements = garmentConfig.measurements;
  return (
    <section style={{ ...cardStyle, marginTop: "24px", padding: "22px" }} aria-labelledby="garment-info-heading">
      <p style={sectionLabelStyle}>Garment Information</p>
      <h2 id="garment-info-heading" style={{ margin: "0 0 5px", fontSize: "22px" }}>Reference Garment — AS Colour 5082</h2>
      <p style={{ margin: "0 0 20px", color: "#999" }}>Oversized Faded T-Shirt</p>
      <dl className="merch-garment-info"><Info label="Print technique" value={PRINT_TECHNIQUE} /><Info label="Maximum print area" value={'12" × 16"'} /><Info label="Front logo" value={'4.0" wide'} /><Info label="Back artwork" value={'11.0" wide'} /><Info label="Front print-area usage" value={`${(IDENTITY_FRONT_PRINT_WIDTH_RATIO * 100).toFixed(1)}%`} /><Info label="Back print-area usage" value={`${(IDENTITY_BACK_PRINT_WIDTH_RATIO * 100).toFixed(1)}%`} /><Info label="Reference size" value={measurements.measurementReferenceSize} /><Info label="Garment dimensions" value={`${measurements.garmentWidthInches.toFixed(2)}" × ${measurements.garmentLengthInches.toFixed(2)}"`} /></dl>
    </section>
  );
}

function createTrueSizeDebug(view) {
  const desiredPrintWidth = view === "front" ? IDENTITY_FRONT_PRINT_WIDTH_INCHES : IDENTITY_BACK_PRINT_WIDTH_INCHES;
  return {
    printTechnique: PRINT_TECHNIQUE,
    maximumPrintWidthInches: PRINTFUL_DTFLEX_MAX_WIDTH_INCHES,
    desiredPrintWidth,
    calculatedArtworkPercent: (desiredPrintWidth / PRINTFUL_DTFLEX_MAX_WIDTH_INCHES) * 100
  };
}

function getIdentityPrintDimensions(view) {
  return calculatePrintDimensions({
    artworkAspectRatio: view === "front" ? IDENTITY_FRONT_VISIBLE_ASPECT_RATIO : IDENTITY_BACK_VISIBLE_ASPECT_RATIO_ESTIMATE,
    requestedWidthInches: view === "front" ? IDENTITY_FRONT_PRINT_WIDTH_INCHES : IDENTITY_BACK_PRINT_WIDTH_INCHES,
    maxWidthInches: PRINTFUL_DTFLEX_MAX_WIDTH_INCHES,
    maxHeightInches: PRINTFUL_DTFLEX_MAX_HEIGHT_INCHES,
    heightIsEstimate: view === "back"
  });
}

function Info({ label, value }) { return <div><dt style={{ color: "#777", fontSize: "10px", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".8px" }}>{label}</dt><dd style={{ margin: "6px 0 0" }}>{value}</dd></div>; }

function getArtworkUrl(urls, garment, view) { return urls[`${view}${garment.artworkTone === "dark" ? "Dark" : "Light"}`]; }

function useSvgBlobUrl(svgString) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    const nextUrl = URL.createObjectURL(new Blob([svgString], { type: "image/svg+xml;charset=utf-8" }));
    setUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [svgString]);
  return url;
}

function loadPreferences() {
  const defaults = cloneDefaults();
  if (typeof localStorage === "undefined") return defaults;
  try {
    const stored = JSON.parse(localStorage.getItem(MOCKUP_STORAGE_KEY));
    if (!stored || typeof stored !== "object") return defaults;
    const storedSchemaVersion = Number(stored.schemaVersion) || 1;
    const calibrationOverrides = sanitizeCalibrationOverrides(stored.calibrationOverrides);
    if (storedSchemaVersion < 4 && calibrationOverrides[DEFAULT_GARMENT_ID]) {
      delete calibrationOverrides[DEFAULT_GARMENT_ID].front;
    }
    return {
      ...defaults,
      selectedGarment: stored.selectedGarment === DEFAULT_GARMENT_ID ? stored.selectedGarment : defaults.selectedGarment,
      selectedColor: GARMENT_COLORS.some((color) => color.id === stored.selectedColor) ? stored.selectedColor : defaults.selectedColor,
      selectedView: stored.selectedView === "front" || stored.selectedView === "back" ? stored.selectedView : defaults.selectedView,
      frontPlacement: storedSchemaVersion < 4 ? { ...defaults.frontPlacement } : sanitizePlacement(stored.frontPlacement, defaults.frontPlacement, IDENTITY_FRONT_PLACEMENT_LIMITS),
      backPlacement: sanitizePlacement(stored.backPlacement, defaults.backPlacement, IDENTITY_BACK_PLACEMENT_LIMITS),
      calibrationOverrides,
      calibrateMode: stored.calibrateMode === true,
      previewSizeMode: stored.previewSizeMode === "trueSize" ? "trueSize" : "visual",
      zoom: ["fit", "50", "100", "150", "200"].includes(stored.zoom) ? stored.zoom : "fit",
      showPrintBoundary: stored.showPrintBoundary === true,
      showRulers: stored.showRulers === true,
      showMeasurements: stored.showMeasurements === true,
      showFrontAndBack: stored.showFrontAndBack === true,
      printNotes: sanitizePrintNotes(stored.printNotes),
      readinessChecks: sanitizeReadiness(stored.readinessChecks),
      schemaVersion: MOCKUP_SCHEMA_VERSION
    };
  } catch (error) {
    console.warn("[Merch Engine] Ignoring malformed Mockup Studio preferences.", error);
    return defaults;
  }
}

function sanitizePlacement(value, defaults, limits) {
  if (!value || typeof value !== "object") return { ...defaults };
  return Object.keys(defaults).reduce((result, key) => {
    const numeric = Number(value[key]);
    result[key] = Number.isFinite(numeric) && limits[key] ? clamp(numeric, limits[key]) : defaults[key];
    return result;
  }, {});
}

function sanitizeCalibrationOverrides(overrides) {
  const source = overrides?.[DEFAULT_GARMENT_ID];
  if (!source || typeof source !== "object") return {};
  const config = getMerchGarmentConfig(DEFAULT_GARMENT_ID);
  return {
    [DEFAULT_GARMENT_ID]: ["front", "back"].reduce((result, view) => {
      if (!source[view]) return result;
      const defaults = getConfiguredGarmentView(config, view);
      result[view] = {
        printArea: sanitizeGroup(source[view].printArea, defaults.printArea, GARMENT_CALIBRATION_LIMITS.printArea),
        calibration: sanitizeGroup(source[view].calibration, defaults.calibration, GARMENT_CALIBRATION_LIMITS.calibration)
      };
      return result;
    }, {})
  };
}

function sanitizeGroup(value, defaults, limits) {
  return Object.keys(defaults).reduce((result, key) => {
    const numeric = Number(value?.[key]);
    result[key] = Number.isFinite(numeric) ? clamp(numeric, limits[key]) : defaults[key];
    return result;
  }, {});
}

function sanitizePrintNotes(value) {
  const statuses = ["Draft", "Needs Review", "Approved for Sample", "Sample Ordered", "Sample Approved"];
  return { approvalStatus: statuses.includes(value?.approvalStatus) ? value.approvalStatus : "Needs Review", notes: typeof value?.notes === "string" ? value.notes : "", lastUpdated: typeof value?.lastUpdated === "string" ? value.lastUpdated : null };
}

function sanitizeReadiness(value) { return { frontBoundaryReviewed: value?.frontBoundaryReviewed === true, backBoundaryReviewed: value?.backBoundaryReviewed === true, frontPlacementReviewed: value?.frontPlacementReviewed === true, backPlacementReviewed: value?.backPlacementReviewed === true, printNotesReviewed: value?.printNotesReviewed === true }; }

function clamp(value, limit) { return Math.min(limit.max, Math.max(limit.min, value)); }
function cloneDefaults() { return { ...DEFAULT_PREFERENCES, frontPlacement: { ...IDENTITY_FRONT_DEFAULT_PLACEMENT }, backPlacement: { ...IDENTITY_BACK_DEFAULT_PLACEMENT }, calibrationOverrides: {}, printNotes: { ...DEFAULT_PREFERENCES.printNotes }, readinessChecks: { ...DEFAULT_PREFERENCES.readinessChecks } }; }
function MockupResponsiveStyles() { return <style>{`.merch-mockup-layout{display:grid;grid-template-columns:minmax(0,1.25fr) minmax(320px,.75fr);gap:24px;align-items:start}.merch-main-mockups{display:grid;grid-template-columns:1fr;gap:18px}.merch-main-mockups.split{grid-template-columns:repeat(2,minmax(0,1fr))}.merch-mockup-comparison{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px}.merch-print-lab-panels{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:22px}.merch-garment-info{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:18px;margin:0}.merch-zoom-viewport{overscroll-behavior:contain}@media(max-width:900px){.merch-mockup-layout,.merch-print-lab-panels{grid-template-columns:1fr}.merch-mockup-comparison{grid-template-columns:repeat(2,minmax(0,1fr))}.merch-garment-info{grid-template-columns:repeat(3,minmax(0,1fr))}}@media(max-width:620px){.merch-main-mockups.split,.merch-mockup-comparison{grid-template-columns:1fr}.merch-garment-info{grid-template-columns:repeat(2,minmax(0,1fr))}}`}</style>; }
const mockupLabelStyle = { margin: "0 0 12px", color: "#aaa", fontSize: "11px", fontWeight: 900, letterSpacing: "1.2px", textTransform: "uppercase" };

export default MerchMockupStudio;
